import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import { firstUnseenEvent } from '../../../shared/network/visual-history.js';
import type { UnitAnimator } from './unit-animator.js';
import { weaponVisual } from '../../../content/equipment/visuals.js';
import { CHARACTER_CONTACT, shotAction } from './character-animator.js';
import { hasOwnFlight, isShepherdShot } from '../../../content/spells/effects.js';
import { isMonsterSpell } from '../combat/monster-channels.js';
export { CharacterAnimator, CHARACTER_ACTION_SECONDS, CHARACTER_FALL_SECONDS } from './character-animator.js';
export { createCharacterAnimator } from './create-character-animator.js';
export type { CharacterAction } from './character-animator.js';
/** Deduplicates authoritative cues, excluding historical events on join/restart. */
export class CharacterAnimationEvents {
  private revision = -1;
  private eventId = 0;
  private projectileId = 0;
  /** Game time each actor's early-started swing reaches contact, when its held cue arrives (see `StrikeTiming`). */
  private swung = new Map<string, number>();
  constructor(private animators: Map<string, UnitAnimator>) {}
  /** A blow without a windup: the clip starts now, while its cue is held until contact `lead` seconds later. */
  swing(actorId: string, toward: { x: number; z: number }, at: number, lead: number) {
    this.animators.get(actorId)?.play('slash', at, toward, 0, undefined, lead);
    this.swung.set(actorId, at + lead);
  }
  receive(snapshot: WorldSnapshot) {
    if (snapshot.revision !== this.revision) {
      this.revision = snapshot.revision; this.swung.clear();
      this.eventId = firstUnseenEvent(snapshot);
      this.projectileId = Math.max(0, ...snapshot.projectiles.map(shot => shot.id));
      for (const animator of this.animators.values()) animator.reset();
      return;
    }
    for (const event of snapshot.visualEvents) {
      if (event.id <= this.eventId) continue;
      this.eventId = event.id;
      // A held swing is already on screen: its cue arrives at contact and must not restart it.
      const contact = this.swung.get(event.actorId) ?? -Infinity;
      if (event.kind === 'strike' && !(snapshot.gameTime > contact - .2 && snapshot.gameTime < contact + 1)) this.animators.get(event.actorId)?.play('slash', snapshot.gameTime, event.to);
      if (event.kind === 'impact' && event.damage > 0) this.animators.get(event.targetId)?.play('hit', snapshot.gameTime, event.from, 0, { damage: event.damage, type: event.damageType, critical: event.critical });
      // damage is post-mitigation; hpDamage is clipped to remaining HP and would discard overkill force.
      if (event.kind === 'impact' && event.lethal) this.animators.get(event.targetId)?.deathImpact?.(snapshot.gameTime, event.from, { damage: event.damage, type: event.damageType, critical: event.critical });
      // A knockdown given by someone without a blow (the scenario viewer's preview) pushes the fall away from them.
      if (event.kind === 'condition' && event.label === 'PRONE' && event.actorId !== event.targetId) this.animators.get(event.targetId)?.shove?.(snapshot.gameTime, event.from);
      if (event.kind === 'block') this.animators.get(event.targetId)?.play('block', snapshot.gameTime, event.from);
      if (event.kind === 'interpose') this.animators.get(event.actorId)?.play('block', snapshot.gameTime, event.to);
    }
    // Flight means the shot already fired: show release/recovery, rather than delay it with a new draw.
    for (const shot of snapshot.projectiles) if (shot.id > this.projectileId) {
      if (hasOwnFlight(shot.attackId) || isMonsterSpell(shot.attackId) || isShepherdShot(shot.attackId)) continue; // Its channel (or the Shepherd's cast) already supplies the release.
      const kind = shotAction(weaponVisual({ id: shot.attackId, mode: 'ranged' }).style);
      this.animators.get(shot.actorId)?.play(kind, snapshot.gameTime, shot.to, CHARACTER_CONTACT[kind]);
    }
    this.projectileId = Math.max(this.projectileId, ...snapshot.projectiles.map(shot => shot.id));
  }
}
